import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, Users, Image, Calendar, MapPin, Check, Settings, Shield, Upload, Camera, X, Clock, Loader2, Infinity } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { useGetEventDetailsQuery, useUpdateEventMutation } from '../../../store/services/api';
import type { Event, UpdateEventRequest } from '../../../types/event';

const EditEvent = () => {
  const navigate = useNavigate();
  const { url } = useParams<{ url: string }>();
  
  const { 
    data: event,
    isLoading,
    error: detailsError
  } = useGetEventDetailsQuery(url || '', { skip: !url });

  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();

  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [basicInfo, setBasicInfo] = useState({
    title: '',
    description: '',
    duration: '7days',
    location: ''
  });

  const [settings, setSettings] = useState({
    allow_photo_views: true,
    allow_guest_uploads: true,
    has_password: false,
    password: ''
  });

  const durationOptions = [
    { value: '7days', label: '7 days' },
    { value: '14days', label: '14 days' },
    { value: '21days', label: '21 days' },
    { value: '30days', label: '30 days' },
    { value: 'unlimited', label: 'Unlimited' },
  ];

  // Update form when event details are loaded
  useEffect(() => {
    if (event) {
      setBasicInfo({
        title: event.title || '',
        description: event.description || '',
        duration: event.duration || '7days',
        location: event.location || ''
      });
      
      setSettings({
        allow_photo_views: event.allow_photo_views,
        allow_guest_uploads: event.allow_guest_uploads,
        has_password: event.has_password,
        password: '' // API doesn't return password
      });
    }
  }, [event]);

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBasicInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSettingsChange = (key: keyof typeof settings, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleUpdateEvent = async () => {
    if (!url) return;
    
    if (!basicInfo.title || !basicInfo.location) {
      setErrors({ 
        title: !basicInfo.title ? 'Title is required' : '',
        location: !basicInfo.location ? 'Location is required' : ''
      });
      return;
    }

    if (settings.has_password && !settings.password && !event?.has_password) {
      setErrors({ password: 'Please provide a password' });
      return;
    }

    try {
      const updateData = {
        url,
        data: {
          title: basicInfo.title,
          description: basicInfo.description,
          location: basicInfo.location,
          has_password: settings.has_password,
          password: settings.password,
          allow_photo_views: settings.allow_photo_views,
          allow_guest_uploads: settings.allow_guest_uploads,
          duration: basicInfo.duration,
        } as UpdateEventRequest
      };

      const updatedEvent = await updateEvent(updateData).unwrap();
      
      // Navigate to the event detail page
      navigate(`/dashboard/events/${updatedEvent.url}`);
    } catch (error) {
      console.error('Failed to update event:', error);
      setErrors({ general: 'Failed to update event. Please try again.' });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto">
        <div className="bg-white p-8 rounded-lg border border-gray-100 text-center my-8">
          <Loader2 className="w-8 h-8 text-[#FF4444] mx-auto animate-spin mb-4" />
          <h3 className="text-base font-medium mb-1">Loading event details...</h3>
          <p className="text-sm text-gray-500">Please wait while we fetch the event information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      {/* Header */}
      <div className="bg-white p-5 rounded-lg border border-gray-100 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Link 
            to="/dashboard/events" 
            className="inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-xs group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
            Back to collections
          </Link>
        </div>
        <h1 className="text-xl font-semibold mb-1">Edit Event</h1>
        <p className="text-sm text-gray-500">Update your event details and settings</p>
      </div>

      {/* Main Form Content */}
      <div className="space-y-6">
        {/* Event Details Section */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-base font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#FF4444]" />
              Event Details
            </h2>
            <p className="text-xs text-gray-500">Update the basic information about your event</p>
          </div>

          <div className="p-5 space-y-4">
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-md text-red-600 text-sm mb-4">
                {errors.general}
              </div>
            )}
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Event Name <span className="text-[#FF4444]">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={basicInfo.title}
                onChange={handleBasicInfoChange}
                className={`w-full px-3 py-2.5 rounded-md border ${errors.title ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'} focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all text-sm`}
                placeholder="Enter event name"
                required
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-500">{errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Event Duration <span className="text-[#FF4444]">*</span>
              </label>
              <div className="relative">
                <select
                  name="duration"
                  value={basicInfo.duration}
                  onChange={handleBasicInfoChange}
                  className="w-full appearance-none px-3 py-2.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all pr-8 bg-white text-sm"
                  required
                >
                  {durationOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <Calendar className="w-4 h-4 text-gray-500" />
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {basicInfo.duration === 'unlimited' 
                  ? 'The gallery will remain active indefinitely' 
                  : 'After this period, the gallery will be archived'}
              </p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Location <span className="text-[#FF4444]">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="location"
                  value={basicInfo.location}
                  onChange={handleBasicInfoChange}
                  className={`w-full px-3 py-2.5 pl-9 rounded-md border ${errors.location ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'} focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all text-sm`}
                  placeholder="Enter event location"
                  required
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <MapPin className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              {errors.location && (
                <p className="mt-1 text-xs text-red-500">{errors.location}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                name="description"
                value={basicInfo.description}
                onChange={handleBasicInfoChange}
                rows={3}
                className="w-full px-3 py-2.5 rounded-md border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all resize-none text-sm"
                placeholder="Describe your event..."
              />
              <p className="mt-1 text-xs text-gray-500">Add details to help your guests understand what this event is about</p>
            </div>
          </div>
        </div>

        {/* Event Settings Section */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-base font-medium flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#FF4444]" />
              Event Settings
            </h2>
            <p className="text-xs text-gray-500">Configure your event's visibility and sharing options</p>
          </div>

          <div className="p-5 space-y-4">
            {/* Photo View Setting */}
            <div className="p-3 border border-gray-200 rounded-md hover:border-[#FF4444] transition-colors bg-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                  <Eye className="w-4 h-4 text-[#FF4444]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Allow Photo Views</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.allow_photo_views}
                        onChange={(e) => handleSettingsChange('allow_photo_views', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#FF4444]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FF4444]"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When disabled, photos will not be viewable with the link. People with the link cannot view photos until you enable this setting.
                  </p>
                </div>
              </div>
            </div>

            {/* Guest Uploads Setting */}
            <div className="p-3 border border-gray-200 rounded-md hover:border-[#FF4444] transition-colors bg-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                  <Upload className="w-4 h-4 text-[#FF4444]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Guest Uploads</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.allow_guest_uploads}
                        onChange={(e) => handleSettingsChange('allow_guest_uploads', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#FF4444]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FF4444]"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Guests can upload photos</p>
                </div>
              </div>
            </div>

            {/* Password Protection Setting */}
            <div className="p-3 border border-gray-200 rounded-md hover:border-[#FF4444] transition-colors bg-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-[#FF4444]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">Password Protection</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.has_password}
                        onChange={(e) => handleSettingsChange('has_password', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#FF4444]/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FF4444]"></div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Require a password to access the event</p>

                  {/* Password Input - Only shown when has_password is true */}
                  {settings.has_password && (
                    <div className="mt-3">
                      <input
                        type="password"
                        value={settings.password}
                        onChange={(e) => handleSettingsChange('password', e.target.value)}
                        placeholder={event?.has_password ? "Enter new password (leave empty to keep current)" : "Enter event password"}
                        className={`w-full px-3 py-2 text-sm rounded-md border ${errors.password ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'} focus:outline-none focus:ring-1 focus:ring-[#FF4444] focus:border-transparent transition-all`}
                      />
                      {errors.password && (
                        <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {event?.has_password 
                          ? "Leave empty to keep the current password, or enter a new one to change it" 
                          : "Guests will need this password to view the event"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Unlimited Photos Info */}
            <div className="p-3 bg-[#FFEEEE]/20 rounded-md border border-[#FF4444]/10">
              <div className="flex items-start gap-2">
                <Infinity className="w-3.5 h-3.5 text-[#FF4444] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-700">
                  Your plan includes unlimited photo uploads for all events. Each upload consumes one image credit from your account.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Event Preview Section */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-base font-medium flex items-center gap-2">
              <Check className="w-4 h-4 text-[#FF4444]" />
              Event Preview
            </h2>
            <p className="text-xs text-gray-500">Review your event details before updating</p>
          </div>

          <div className="p-5 space-y-4">
            {/* Event Details Summary */}
            <div className="bg-gray-50 p-3 rounded-md">
              <h3 className="text-xs font-medium mb-2 text-gray-900 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#FF4444]" />
                Event Summary
              </h3>
              
              <div className="space-y-2.5">
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Event Name</label>
                  <p className="text-sm font-medium">{basicInfo.title || 'Not specified yet'}</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Duration</label>
                  <p className="text-sm">
                    {durationOptions.find(option => option.value === basicInfo.duration)?.label || '7 days'}
                  </p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Location</label>
                  <p className="text-sm">{basicInfo.location || 'Not specified yet'}</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Privacy</label>
                  <p className="text-sm">{settings.allow_photo_views ? 'Public Photo Viewing' : 'Private Photo Viewing'}</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Guest Uploads</label>
                  <p className="text-sm">{settings.allow_guest_uploads ? 'Enabled' : 'Disabled'}</p>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Password Protection</label>
                  <p className="text-sm">{settings.has_password ? 'Enabled' : 'Disabled'}</p>
                </div>
                
                {event && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-0.5">Event URL</label>
                    <p className="text-sm font-medium text-[#FF4444]">ourphotos.co/e/{event.url}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <Button 
                onClick={handleUpdateEvent} 
                className="w-full h-9 text-sm"
                disabled={isUpdating || !basicInfo.title || !basicInfo.location || (settings.has_password && !settings.password && !event?.has_password)}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Updating Event...
                  </>
                ) : (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1.5" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard/events')} 
                className="w-full h-9 text-sm"
                disabled={isUpdating}
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEvent;